import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { apiAdmin, setSuperAdminToken, getDisplayMessage } from '@/api/apiClient';
import { ChevronUp, ChevronDown, Trash2, Play } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';

const PROVIDERS = [
  { value: 'openai', label: 'ChatGPT (OpenAI)' },
  { value: 'anthropic', label: 'Claude (Anthropic)' },
  { value: 'perplexity', label: 'Perplexity' },
  { value: 'zai', label: 'Z.AI' },
  { value: 'gpt4all', label: 'GPT4All (local)' },
  { value: 'gemini', label: 'Gemini 1.5 Flash (Google AI Studio)' },
  { value: 'huggingface', label: 'Hugging Face (Inference API)' },
  { value: 'groq', label: 'Groq' },
  { value: 'joingonka', label: 'JoinGonka.IA (Gonka Gateway)' },
  { value: 'openrouter', label: 'OpenRouter (Nemotron 3 Ultra free)' },
  { value: 'clod', label: 'CLōD (clod.io)' },
];

const PROVIDERS_BY_VALUE = Object.fromEntries(PROVIDERS.map((p) => [p.value, p]));

const PROVIDERS_NEED_API_KEY = ['openai', 'anthropic', 'perplexity', 'zai', 'gemini', 'huggingface', 'groq', 'joingonka', 'openrouter', 'clod'];

/** Não envia string vazia — evita apagar API key já salva no servidor. */
function apiKeyForPayload(formValue) {
  if (!formValue || formValue === '********') return undefined;
  const trimmed = String(formValue).trim();
  return trimmed || undefined;
}

const defaultForm = {
  providerOrder: [],
  openai: { apiKey: '', baseUrl: 'https://api.openai.com/v1', model: 'gpt-4o-mini' },
  anthropic: { apiKey: '', baseUrl: 'https://api.anthropic.com', model: 'claude-3-5-sonnet-latest' },
  perplexity: { apiKey: '', baseUrl: 'https://api.perplexity.ai', model: 'sonar' },
  zai: { apiKey: '', baseUrl: 'https://api.z.ai/api/paas/v4', model: 'glm-5' },
  gpt4all: { baseUrl: 'http://localhost:4891/v1', model: 'Phi-3 Mini Instruct' },
  gemini: { apiKey: '', baseUrl: 'https://generativelanguage.googleapis.com/v1beta', model: 'gemini-2.5-flash' },
  huggingface: { apiKey: '', baseUrl: 'https://api-inference.huggingface.co', model: 'meta-llama/Llama-3.1-8B-Instruct' },
  groq: { apiKey: '', baseUrl: 'https://api.groq.com/openai/v1', model: 'llama-3.1-70b-versatile' },
  joingonka: {
    apiKey: '',
    baseUrl: 'https://gate.joingonka.ai/v1',
    model: 'Qwen/Qwen3-235B-A22B-Instruct-2507-FP8',
  },
  openrouter: {
    apiKey: '',
    baseUrl: 'https://openrouter.ai/api/v1',
    model: 'nvidia/nemotron-3-ultra-550b-a55b:free',
  },
  clod: {
    apiKey: '',
    baseUrl: 'https://api.clod.io/v1',
    model: 'gpt-4.1',
  },
};

export default function AdminConfigEditalAI() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [erro, setErro] = useState('');
  const [form, setForm] = useState(defaultForm);

  const carregar = async () => {
    setLoading(true);
    setErro('');
    try {
      const data = await apiAdmin.get('/api/admin/config/edital-ai');
      setConfig(data);
      const order = Array.isArray(data.providerOrder) && data.providerOrder.length
        ? data.providerOrder
        : data.provider && data.provider !== 'none'
          ? [data.provider]
          : [];
      setForm({
        providerOrder: order,
        openai: {
          apiKey: data.openai?.apiKeyConfigured ? '********' : (data.openai?.apiKey || defaultForm.openai.apiKey),
          baseUrl: data.openai?.baseUrl || defaultForm.openai.baseUrl,
          model: data.openai?.model || defaultForm.openai.model,
        },
        anthropic: {
          apiKey: data.anthropic?.apiKeyConfigured ? '********' : (data.anthropic?.apiKey || defaultForm.anthropic.apiKey),
          baseUrl: data.anthropic?.baseUrl || defaultForm.anthropic.baseUrl,
          model: data.anthropic?.model || defaultForm.anthropic.model,
        },
        perplexity: {
          apiKey: data.perplexity?.apiKeyConfigured ? '********' : (data.perplexity?.apiKey || defaultForm.perplexity.apiKey),
          baseUrl: data.perplexity?.baseUrl || defaultForm.perplexity.baseUrl,
          model: data.perplexity?.model || defaultForm.perplexity.model,
        },
        zai: {
          apiKey: data.zai?.apiKeyConfigured ? '********' : (data.zai?.apiKey || defaultForm.zai.apiKey),
          baseUrl: data.zai?.baseUrl || defaultForm.zai.baseUrl,
          model: data.zai?.model || defaultForm.zai.model,
        },
        gpt4all: {
          baseUrl: data.gpt4all?.baseUrl || defaultForm.gpt4all.baseUrl,
          model: data.gpt4all?.model || defaultForm.gpt4all.model,
        },
        gemini: {
          apiKey: data.gemini?.apiKeyConfigured ? '********' : (data.gemini?.apiKey || defaultForm.gemini.apiKey),
          baseUrl: data.gemini?.baseUrl || defaultForm.gemini.baseUrl,
          model: data.gemini?.model || defaultForm.gemini.model,
        },
        huggingface: {
          apiKey: data.huggingface?.apiKeyConfigured ? '********' : (data.huggingface?.apiKey || defaultForm.huggingface.apiKey),
          baseUrl: data.huggingface?.baseUrl || defaultForm.huggingface.baseUrl,
          model: data.huggingface?.model || defaultForm.huggingface.model,
        },
        groq: {
          apiKey: data.groq?.apiKeyConfigured ? '********' : (data.groq?.apiKey || defaultForm.groq.apiKey),
          baseUrl: data.groq?.baseUrl || defaultForm.groq.baseUrl,
          model: data.groq?.model || defaultForm.groq.model,
        },
        joingonka: {
          apiKey: data.joingonka?.apiKeyConfigured ? '********' : (data.joingonka?.apiKey || defaultForm.joingonka.apiKey),
          baseUrl: data.joingonka?.baseUrl || defaultForm.joingonka.baseUrl,
          model: data.joingonka?.model || defaultForm.joingonka.model,
        },
        openrouter: {
          apiKey: data.openrouter?.apiKeyConfigured ? '********' : (data.openrouter?.apiKey || defaultForm.openrouter.apiKey),
          baseUrl: data.openrouter?.baseUrl || defaultForm.openrouter.baseUrl,
          model: data.openrouter?.model || defaultForm.openrouter.model,
        },
        clod: {
          apiKey: data.clod?.apiKeyConfigured ? '********' : (data.clod?.apiKey || defaultForm.clod.apiKey),
          baseUrl: data.clod?.baseUrl || defaultForm.clod.baseUrl,
          model: data.clod?.model || defaultForm.clod.model,
        },
      });
    } catch (err) {
      if (err?.status === 401) {
        setSuperAdminToken(null);
        window.location.href = '/admin';
        return;
      }
      setErro(getDisplayMessage(err, 'Erro ao carregar configuração. Tente novamente.'));
      toast.error('Erro ao carregar configuração');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (!name || typeof name !== 'string') return;
    const [provider, field] = name.split('.');
    if (provider && field && form[provider]) {
      setForm((prev) => ({
        ...prev,
        [provider]: { ...prev[provider], [field]: value },
      }));
    }
    setErro('');
  };

  const addProvider = (value) => {
    if (!value || form.providerOrder.includes(value)) return;
    setForm((prev) => ({ ...prev, providerOrder: [...prev.providerOrder, value] }));
    setErro('');
  };

  const removeProvider = (value) => {
    setForm((prev) => ({ ...prev, providerOrder: prev.providerOrder.filter((p) => p !== value) }));
    setErro('');
  };

  const moveProvider = (index, direction) => {
    const next = [...form.providerOrder];
    const j = index + direction;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    setForm((prev) => ({ ...prev, providerOrder: next }));
    setErro('');
  };

  const buildPayload = () => {
    const payload = { providerOrder: form.providerOrder };
    form.providerOrder.forEach((p) => {
      if (p === 'openai') {
        payload.openai = {
          baseUrl: form.openai.baseUrl?.trim() || undefined,
          model: form.openai.model?.trim() || undefined,
          apiKey: apiKeyForPayload(form.openai.apiKey),
        };
      }
      if (p === 'anthropic') {
        payload.anthropic = {
          baseUrl: form.anthropic.baseUrl?.trim() || undefined,
          model: form.anthropic.model?.trim() || undefined,
          apiKey: apiKeyForPayload(form.anthropic.apiKey),
        };
      }
      if (p === 'perplexity') {
        payload.perplexity = {
          baseUrl: form.perplexity.baseUrl?.trim() || undefined,
          model: form.perplexity.model?.trim() || undefined,
          apiKey: apiKeyForPayload(form.perplexity.apiKey),
        };
      }
      if (p === 'zai') {
        payload.zai = {
          baseUrl: form.zai.baseUrl?.trim() || undefined,
          model: form.zai.model?.trim() || undefined,
          apiKey: apiKeyForPayload(form.zai.apiKey),
        };
      }
      if (p === 'gpt4all') {
        payload.gpt4all = {
          baseUrl: form.gpt4all.baseUrl?.trim() || undefined,
          model: form.gpt4all.model?.trim() || undefined,
        };
      }
      if (p === 'gemini') {
        payload.gemini = {
          baseUrl: form.gemini.baseUrl?.trim() || undefined,
          model: form.gemini.model?.trim() || undefined,
          apiKey: apiKeyForPayload(form.gemini.apiKey),
        };
      }
      if (p === 'huggingface') {
        payload.huggingface = {
          baseUrl: form.huggingface.baseUrl?.trim() || undefined,
          model: form.huggingface.model?.trim() || undefined,
          apiKey: apiKeyForPayload(form.huggingface.apiKey),
        };
      }
      if (p === 'groq') {
        payload.groq = {
          baseUrl: form.groq.baseUrl?.trim() || undefined,
          model: form.groq.model?.trim() || undefined,
          apiKey: apiKeyForPayload(form.groq.apiKey),
        };
      }
      if (p === 'joingonka') {
        payload.joingonka = {
          baseUrl: form.joingonka.baseUrl?.trim() || undefined,
          model: form.joingonka.model?.trim() || undefined,
          apiKey: apiKeyForPayload(form.joingonka.apiKey),
        };
      }
      if (p === 'openrouter') {
        payload.openrouter = {
          baseUrl: form.openrouter.baseUrl?.trim() || undefined,
          model: form.openrouter.model?.trim() || undefined,
          apiKey: apiKeyForPayload(form.openrouter.apiKey),
        };
      }
      if (p === 'clod') {
        payload.clod = {
          baseUrl: form.clod.baseUrl?.trim() || undefined,
          model: form.clod.model?.trim() || undefined,
          apiKey: apiKeyForPayload(form.clod.apiKey),
        };
      }
    });
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErro('');
    try {
      const payload = buildPayload();
      const data = await apiAdmin.put('/api/admin/config/edital-ai', payload);
      setConfig(data);
      setForm((prev) => {
        const nextOrder = Array.isArray(data.providerOrder) ? data.providerOrder : prev.providerOrder;
        return {
          ...prev,
          providerOrder: nextOrder,
        openai: {
          apiKey: data.openai?.apiKeyConfigured ? '********' : (data.openai?.apiKey || ''),
          baseUrl: data.openai?.baseUrl || prev.openai.baseUrl,
          model: data.openai?.model || prev.openai.model,
        },
        anthropic: {
          apiKey: data.anthropic?.apiKeyConfigured ? '********' : (data.anthropic?.apiKey || ''),
          baseUrl: data.anthropic?.baseUrl || prev.anthropic.baseUrl,
          model: data.anthropic?.model || prev.anthropic.model,
        },
        perplexity: {
          apiKey: data.perplexity?.apiKeyConfigured ? '********' : (data.perplexity?.apiKey || ''),
          baseUrl: data.perplexity?.baseUrl || prev.perplexity.baseUrl,
          model: data.perplexity?.model || prev.perplexity.model,
        },
        zai: {
          apiKey: data.zai?.apiKeyConfigured ? '********' : (data.zai?.apiKey || ''),
          baseUrl: data.zai?.baseUrl || prev.zai.baseUrl,
          model: data.zai?.model || prev.zai.model,
        },
        gpt4all: {
          baseUrl: data.gpt4all?.baseUrl || prev.gpt4all.baseUrl,
          model: data.gpt4all?.model || prev.gpt4all.model,
        },
        gemini: {
          apiKey: data.gemini?.apiKeyConfigured ? '********' : (data.gemini?.apiKey || ''),
          baseUrl: data.gemini?.baseUrl || prev.gemini.baseUrl,
          model: data.gemini?.model || prev.gemini.model,
        },
        huggingface: {
          apiKey: data.huggingface?.apiKeyConfigured ? '********' : (data.huggingface?.apiKey || ''),
          baseUrl: data.huggingface?.baseUrl || prev.huggingface.baseUrl,
          model: data.huggingface?.model || prev.huggingface.model,
        },
        groq: {
          apiKey: data.groq?.apiKeyConfigured ? '********' : (data.groq?.apiKey || ''),
          baseUrl: data.groq?.baseUrl || prev.groq.baseUrl,
          model: data.groq?.model || prev.groq.model,
        },
        joingonka: {
          apiKey: data.joingonka?.apiKeyConfigured ? '********' : (data.joingonka?.apiKey || ''),
          baseUrl: data.joingonka?.baseUrl || prev.joingonka.baseUrl,
          model: data.joingonka?.model || prev.joingonka.model,
        },
        openrouter: {
          apiKey: data.openrouter?.apiKeyConfigured ? '********' : (data.openrouter?.apiKey || ''),
          baseUrl: data.openrouter?.baseUrl || prev.openrouter.baseUrl,
          model: data.openrouter?.model || prev.openrouter.model,
        },
        clod: {
          apiKey: data.clod?.apiKeyConfigured ? '********' : (data.clod?.apiKey || ''),
          baseUrl: data.clod?.baseUrl || prev.clod.baseUrl,
          model: data.clod?.model || prev.clod.model,
        },
      };
      });
      toast.success('Configuração salva com sucesso.');
    } catch (err) {
      setErro(getDisplayMessage(err, 'Erro ao salvar configuração. Tente novamente.'));
      toast.error('Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-2xl mx-auto space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const providerNeedsApiKey = (p) => PROVIDERS_NEED_API_KEY.includes(p);
  const hasApiKeyFor = (p) => config?.[p]?.apiKeyConfigured || (form[p]?.apiKey?.trim() && form[p].apiKey !== '********');
  const canSubmit =
    form.providerOrder.length > 0 &&
    form.providerOrder.every((p) => !providerNeedsApiKey(p) || hasApiKeyFor(p));

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Config. IA Editais</h1>
        <p className="text-slate-600 mt-1">
          Selecione um ou mais provedores na ordem de prioridade. Em caso de erro, o próximo será usado automaticamente (fallback).
        </p>
      </div>

      {erro && (
        <Alert variant="destructive">
          <AlertDescription>{erro}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="pt-6">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Ordem de prioridade (fallback automático)</label>
          <p className="text-xs text-slate-500 mb-2">O primeiro da lista é o principal; em falha, o próximo é tentado sem interromper o processamento.</p>
          <ul className="space-y-2 mb-2">
            {form.providerOrder.map((value, index) => (
              <li key={`${value}-${index}`} className="flex items-center gap-2 py-2 px-3 bg-slate-50 rounded-lg border">
                <span className="text-slate-500 w-6">{index + 1}.</span>
                <span className="flex-1 font-medium text-slate-800">{PROVIDERS_BY_VALUE[value]?.label ?? value}</span>
                <div className="flex gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => moveProvider(index, -1)}
                    disabled={index === 0}
                    title="Subir"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => moveProvider(index, 1)}
                    disabled={index === form.providerOrder.length - 1}
                    title="Descer"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => removeProvider(value)}
                    title="Remover"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
          <div className="flex gap-2 items-center">
            <select
              value=""
              onChange={(e) => { const v = e.target.value; if (v) { addProvider(v); e.target.value = ''; } }}
              className="flex h-9 w-[220px] rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <option value="">Adicionar provedor...</option>
              {PROVIDERS.filter((p) => !form.providerOrder.includes(p.value)).map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
        </div>

        {form.providerOrder.includes('openai') && (
          <div className="space-y-4 border-t border-slate-200 pt-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">API Key (OpenAI)</label>
              <input
                type="password"
                name="openai.apiKey"
                value={form.openai.apiKey}
                onChange={handleChange}
                placeholder={config?.openai?.apiKeyConfigured ? 'Deixe em branco para manter' : 'sk-...'}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">URL base</label>
              <input
                type="url"
                name="openai.baseUrl"
                value={form.openai.baseUrl}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Modelo</label>
              <input
                type="text"
                name="openai.model"
                value={form.openai.model}
                onChange={handleChange}
                placeholder="gpt-4o-mini"
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              />
            </div>
          </div>
        )}

        {form.providerOrder.includes('anthropic') && (
          <div className="space-y-4 border-t border-slate-200 pt-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">API Key (Anthropic)</label>
              <input
                type="password"
                name="anthropic.apiKey"
                value={form.anthropic.apiKey}
                onChange={handleChange}
                placeholder={config?.anthropic?.apiKeyConfigured ? 'Deixe em branco para manter' : 'sk-ant-...'}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">URL base</label>
              <input
                type="url"
                name="anthropic.baseUrl"
                value={form.anthropic.baseUrl}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Modelo</label>
              <input
                type="text"
                name="anthropic.model"
                value={form.anthropic.model}
                onChange={handleChange}
                placeholder="claude-3-5-sonnet-latest"
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              />
            </div>
          </div>
        )}

        {form.providerOrder.includes('perplexity') && (
          <div className="space-y-4 border-t border-slate-200 pt-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">API Key (Perplexity)</label>
              <input
                type="password"
                name="perplexity.apiKey"
                value={form.perplexity.apiKey}
                onChange={handleChange}
                placeholder={config?.perplexity?.apiKeyConfigured ? 'Deixe em branco para manter' : 'pplx-...'}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">URL base</label>
              <input
                type="url"
                name="perplexity.baseUrl"
                value={form.perplexity.baseUrl}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Modelo</label>
              <input
                type="text"
                name="perplexity.model"
                value={form.perplexity.model}
                onChange={handleChange}
                placeholder="sonar"
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              />
            </div>
          </div>
        )}

        {form.providerOrder.includes('zai') && (
          <div className="space-y-4 border-t border-slate-200 pt-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">API Key (Z.AI)</label>
              <input
                type="password"
                name="zai.apiKey"
                value={form.zai.apiKey}
                onChange={handleChange}
                placeholder={config?.zai?.apiKeyConfigured ? 'Deixe em branco para manter' : 'Sua chave Z.AI'}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">URL base</label>
              <input
                type="url"
                name="zai.baseUrl"
                value={form.zai.baseUrl}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Modelo</label>
              <input
                type="text"
                name="zai.model"
                value={form.zai.model}
                onChange={handleChange}
                placeholder="glm-5"
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              />
            </div>
          </div>
        )}

        {form.providerOrder.includes('gpt4all') && (
          <div className="space-y-4 border-t border-slate-200 pt-4">
            <p className="text-sm text-slate-600">Nenhuma API Key. O servidor GPT4All deve estar rodando (ex.: app desktop com API ativada na porta 4891).</p>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">URL base</label>
              <input
                type="url"
                name="gpt4all.baseUrl"
                value={form.gpt4all.baseUrl}
                onChange={handleChange}
                placeholder="http://localhost:4891/v1"
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Modelo</label>
              <input
                type="text"
                name="gpt4all.model"
                value={form.gpt4all.model}
                onChange={handleChange}
                placeholder="Phi-3 Mini Instruct"
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              />
            </div>
          </div>
        )}

        {form.providerOrder.includes('gemini') && (
          <div className="space-y-4 border-t border-slate-200 pt-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">API Key (Google AI Studio)</label>
              <input
                type="password"
                name="gemini.apiKey"
                value={form.gemini.apiKey}
                onChange={handleChange}
                placeholder={config?.gemini?.apiKeyConfigured ? 'Deixe em branco para manter' : 'Sua chave do Google AI Studio'}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                autoComplete="off"
              />
              <p className="mt-1 text-xs text-slate-500">
                Obtenha sua chave gratuita em{' '}
                <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  Google AI Studio
                </a>
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">URL base</label>
              <input
                type="url"
                name="gemini.baseUrl"
                value={form.gemini.baseUrl}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Modelo</label>
              <input
                type="text"
                name="gemini.model"
                value={form.gemini.model}
                onChange={handleChange}
                placeholder="gemini-1.5-flash"
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              />
            </div>
          </div>
        )}

        {form.providerOrder.includes('huggingface') && (
          <div className="space-y-4 border-t border-slate-200 pt-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">API Key (Hugging Face)</label>
              <input
                type="password"
                name="huggingface.apiKey"
                value={form.huggingface.apiKey}
                onChange={handleChange}
                placeholder={config?.huggingface?.apiKeyConfigured ? 'Deixe em branco para manter' : 'hf_...'}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                autoComplete="off"
              />
              <p className="mt-1 text-xs text-slate-500">
                Token em{' '}
                <a href="https://huggingface.co/settings/tokens" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  huggingface.co/settings/tokens
                </a>
                {' '}(permissão Inference).
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">URL base</label>
              <input
                type="url"
                name="huggingface.baseUrl"
                value={form.huggingface.baseUrl}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Modelo (ID no Hub)</label>
              <input
                type="text"
                name="huggingface.model"
                value={form.huggingface.model}
                onChange={handleChange}
                placeholder="meta-llama/Llama-3.1-8B-Instruct"
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              />
            </div>
          </div>
        )}

        {form.providerOrder.includes('groq') && (
          <div className="space-y-4 border-t border-slate-200 pt-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">API Key (Groq)</label>
              <input
                type="password"
                name="groq.apiKey"
                value={form.groq.apiKey}
                onChange={handleChange}
                placeholder={config?.groq?.apiKeyConfigured ? 'Deixe em branco para manter' : 'gsk_...'}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                autoComplete="off"
              />
              <p className="mt-1 text-xs text-slate-500">
                Chave em{' '}
                <a href="https://console.groq.com/keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  console.groq.com
                </a>
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">URL base</label>
              <input
                type="url"
                name="groq.baseUrl"
                value={form.groq.baseUrl}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Modelo</label>
              <input
                type="text"
                name="groq.model"
                value={form.groq.model}
                onChange={handleChange}
                placeholder="llama-3.1-70b-versatile"
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              />
            </div>
          </div>
        )}

        {form.providerOrder.includes('joingonka') && (
          <div className="space-y-4 border-t border-slate-200 pt-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">API Key (JoinGonka.IA)</label>
              <input
                type="password"
                name="joingonka.apiKey"
                value={form.joingonka.apiKey}
                onChange={handleChange}
                placeholder={config?.joingonka?.apiKeyConfigured ? 'Deixe em branco para manter' : 'jg-...'}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                autoComplete="off"
              />
              <p className="mt-1 text-xs text-slate-500">
                Chave em{' '}
                <a href="https://gate.joingonka.ai/register" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  gate.joingonka.ai
                </a>
                {' '}(formato jg-...). API compatível com OpenAI.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">URL base</label>
              <input
                type="url"
                name="joingonka.baseUrl"
                value={form.joingonka.baseUrl}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Modelo</label>
              <input
                type="text"
                name="joingonka.model"
                value={form.joingonka.model}
                onChange={handleChange}
                placeholder="Qwen/Qwen3-235B-A22B-Instruct-2507-FP8"
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              />
            </div>
          </div>
        )}

        {form.providerOrder.includes('openrouter') && (
          <div className="space-y-4 border-t border-slate-200 pt-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">API Key (OpenRouter)</label>
              <input
                type="password"
                name="openrouter.apiKey"
                value={form.openrouter.apiKey}
                onChange={handleChange}
                placeholder={config?.openrouter?.apiKeyConfigured ? 'Deixe em branco para manter' : 'sk-or-v1-...'}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                autoComplete="off"
              />
              <p className="mt-1 text-xs text-slate-500">
                Chave em{' '}
                <a href="https://openrouter.ai/settings/keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  openrouter.ai/settings/keys
                </a>
                . API compatível com OpenAI.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">URL base</label>
              <input
                type="url"
                name="openrouter.baseUrl"
                value={form.openrouter.baseUrl}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Modelo</label>
              <input
                type="text"
                name="openrouter.model"
                value={form.openrouter.model}
                onChange={handleChange}
                placeholder="nvidia/nemotron-3-ultra-550b-a55b:free"
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              />
            </div>
          </div>
        )}

        {form.providerOrder.includes('clod') && (
          <div className="space-y-4 border-t border-slate-200 pt-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">API Key (CLōD)</label>
              <input
                type="password"
                name="clod.apiKey"
                value={form.clod.apiKey}
                onChange={handleChange}
                placeholder={config?.clod?.apiKeyConfigured ? 'Deixe em branco para manter' : 'Sua chave CLōD'}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                autoComplete="off"
              />
              <p className="mt-1 text-xs text-slate-500">
                Chave em{' '}
                <a href="https://app.clod.io" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  app.clod.io
                </a>
                . API compatível com OpenAI (50+ modelos).
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">URL base</label>
              <input
                type="url"
                name="clod.baseUrl"
                value={form.clod.baseUrl}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Modelo</label>
              <input
                type="text"
                name="clod.model"
                value={form.clod.model}
                onChange={handleChange}
                placeholder="gpt-4.1"
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              />
            </div>
          </div>
        )}

        <div className="pt-2 flex flex-wrap gap-2 items-center">
          <Button type="submit" disabled={saving || !canSubmit}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={testing || !canSubmit}
            onClick={async () => {
              setTesting(true);
              setErro('');
              try {
                const data = await apiAdmin.post('/api/admin/config/edital-ai/test');
                if (data.ok) {
                  const prov = data.provider ? ` (${data.provider})` : '';
                  toast.success(data.message || `Conexão com o provedor de IA OK${prov}.`);
                } else {
                  toast.error(data.error || 'Falha no teste.');
                }
              } catch (err) {
                const msg = err?.data?.error ?? err?.message ?? 'Erro ao testar. Verifique a API key e o modelo.';
                setErro(msg);
                toast.error(msg);
              } finally {
                setTesting(false);
              }
            }}
          >
            {testing ? 'Testando...' : (
              <>
                <Play className="h-4 w-4 mr-1.5" />
                Testar configuração
              </>
            )}
          </Button>
        </div>
      </form>
        </CardContent>
      </Card>
    </div>
  );
}
